/**
 * BIA Components Orchestrator
 *
 * Script principal que auto-detecta elementos <bia-*> en el DOM
 * y carga dinámicamente el JS del componente correspondiente.
 *
 * Uso en portal externo:
 *   <bia-crossselling doc-id="12345" limit="4"></bia-crossselling>
 *   <bia-widget placeholder="Buscar..."></bia-widget>
 *   <script src="https://bia.lefebvre.es/components/main.js"></script>
 *
 * El orquestador:
 * 1. Detecta la URL base desde donde se cargó (ej. https://bia.lefebvre.es/components/)
 * 2. Escanea el DOM buscando elementos <bia-*>
 * 3. Carga dinámicamente solo los JS necesarios
 * 4. Auto-inyecta api-url en componentes que no lo tengan
 * 5. Observa el DOM para componentes añadidos después de la carga inicial
 */
// Capturar document.currentScript ANTES de la IIFE (solo funciona en ejecución síncrona)
const _biaSelfScript = document.currentScript;

(function () {
  'use strict';

  // --- Registro de componentes conocidos ---
  // Mapea nombre de tag → fichero JS en /components/
  const COMPONENT_MAP = {
    'bia-crossselling': 'crossselling.js',
    'bia-widget': 'widget.js',
    'bia-autocomplete': 'autocomplete.js',
    'bia-results': 'results.js',
    'bia-chat': 'chat.js',
  };

  // --- Determinar URL base ---
  // Usa document.currentScript para identificar con precisión este script.
  // Fallback: buscar el tag que contenga /components/main.js explícitamente
  // (evita confusión con otros scripts del portal que se llamen main.js)
  function extractBase(src) {
    if (!src) return null;
    const idx = src.indexOf('/components/');
    if (idx !== -1) return src.substring(0, idx);
    // Ruta relativa tipo "components/main.js"
    const idx2 = src.indexOf('components/');
    if (idx2 !== -1) {
      const prefix = src.substring(0, idx2);
      return prefix ? prefix.replace(/\/$/, '') : window.location.origin;
    }
    return null;
  }

  function getBaseUrl() {
    // 1. document.currentScript: el script BIA que se está ejecutando ahora
    if (_biaSelfScript) {
      const base = extractBase(_biaSelfScript.getAttribute('src'));
      if (base !== null) return base;
    }
    // 2. Fallback: buscar explícitamente /components/main.js en todos los scripts
    const scripts = document.querySelectorAll('script[src]');
    for (const script of scripts) {
      const src = script.getAttribute('src');
      if (src && src.includes('/components/main.js')) {
        const base = extractBase(src);
        if (base !== null) return base;
      }
    }
    // 3. Último recurso: mismo origen
    return window.location.origin;
  }

  const BASE_URL = getBaseUrl();
  const COMPONENTS_URL = BASE_URL + '/components/';
  const API_KEY = _biaSelfScript ? _biaSelfScript.getAttribute('data-api-key') : null;

  // Extraer ?v= del src del propio main.js para propagarlo a los componentes
  const _selfSrc = _biaSelfScript ? _biaSelfScript.getAttribute('src') : '';
  // Los componentes se piden con la versión del build de BIA (BUILD_ID, inyectado al compilar), no con el
  // ?v= del portal: BIA los sirve con caché de 1 año, así que un results.js nuevo solo llega si cambia su
  // URL, y eso no puede depender de que el portal publique una versión. Sin BUILD_ID (dev) se propaga el ?v= externo.
  const _vMatch = _selfSrc && _selfSrc.match(/[?&]v=([^&]+)/);
  const BUILD_ID = typeof __BIA_BUILD_ID__ !== 'undefined' ? __BIA_BUILD_ID__ : '';
  const CACHE_BUST = BUILD_ID ? '?v=' + BUILD_ID : (_vMatch ? '?v=' + _vMatch[1] : '');

  // Scripts ya cargados o en proceso
  const loadedScripts = new Set();
  const loadingPromises = {};

  // --- Cargar un script dinámicamente ---
  function loadScript(fileName) {
    if (loadedScripts.has(fileName)) {
      return Promise.resolve();
    }
    if (loadingPromises[fileName]) {
      return loadingPromises[fileName];
    }

    const url = COMPONENTS_URL + fileName + CACHE_BUST;
    const promise = new Promise(function (resolve, reject) {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.onload = function () {
        loadedScripts.add(fileName);
        delete loadingPromises[fileName];
        console.log('[BIA] Componente cargado:', fileName);
        resolve();
      };
      script.onerror = function () {
        delete loadingPromises[fileName];
        console.error('[BIA] Error cargando componente:', url);
        reject(new Error('Error cargando ' + url));
      };
      document.head.appendChild(script);
    });

    loadingPromises[fileName] = promise;
    return promise;
  }

  // --- Auto-inyectar atributos del orquestador ---
  function injectAttributes(element) {
    if (!element.getAttribute('api-url')) {
      element.setAttribute('api-url', BASE_URL);
    }
    if (API_KEY && !element.getAttribute('api-key')) {
      element.setAttribute('api-key', API_KEY);
    }
  }

  // --- Procesar un elemento <bia-*> ---
  function processElement(element) {
    const tag = element.tagName.toLowerCase();
    const fileName = COMPONENT_MAP[tag];
    if (!fileName) return;

    injectAttributes(element);
    loadScript(fileName).catch(function () {}); // ya se ha avisado en consola; sin esto queda «Uncaught (in promise)»
  }

  // --- Escanear DOM completo ---
  function scanDOM() {
    var tags = Object.keys(COMPONENT_MAP);
    var selector = tags.join(',');
    var elements = document.querySelectorAll(selector);
    elements.forEach(processElement);
  }

  // --- Observer para elementos añadidos dinámicamente ---
  function observeDOM() {
    if (!window.MutationObserver) return;

    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return; // Solo elementos
          var tag = node.tagName.toLowerCase();
          if (COMPONENT_MAP[tag]) {
            processElement(node);
          }
          // También buscar dentro de los hijos
          var tags = Object.keys(COMPONENT_MAP);
          var selector = tags.join(',');
          var children = node.querySelectorAll ? node.querySelectorAll(selector) : [];
          children.forEach(processElement);
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  // --- Analítica de búsquedas (PWEB-25137) ---
  // API común para los componentes: window.BIA.analytics.
  // - Sin consentimiento de estadística (Cookiebot) no se guarda nada en el navegador:
  //   los eventos van anónimos, enlazados solo por el search_id de la búsqueda.
  // - Con Cookiebot.consent.statistics: session_id (sessionStorage) y client ID de GA (_ga).
  // - Los eventos se envían con navigator.sendBeacon (sobreviven a la navegación);
  //   sendBeacon no admite cabeceras, así que la API key va en el cuerpo.
  var SESSION_KEY = 'bia_sid';

  function hasStatsConsent() {
    var cb = window.Cookiebot;
    return !!(cb && cb.consent && cb.consent.statistics);
  }

  function newId() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
  }

  function getSessionId() {
    if (!hasStatsConsent()) return null;
    try {
      var sid = sessionStorage.getItem(SESSION_KEY);
      if (!sid) {
        sid = newId();
        sessionStorage.setItem(SESSION_KEY, sid);
      }
      return sid;
    } catch (e) {
      return null;
    }
  }

  function getGaClientId() {
    if (!hasStatsConsent()) return null;
    // Cookie _ga = GA1.1.<client_id>  (client_id = número.timestamp)
    var m = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.(\d+\.\d+)/);
    return m ? m[1] : null;
  }

  // Si el usuario retira el consentimiento, se borra la sesión
  window.addEventListener('CookiebotOnDecline', function () {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* sin storage */ }
  });

  /** Parámetros de contexto para añadir a /api/v1/search. */
  function searchParams() {
    var params = { page: window.location.pathname.slice(0, 300) };
    var sid = getSessionId();
    var ga = getGaClientId();
    // «sid» y no «session_id»: la infraestructura de PRO responde 502 a URLs con session_id= (PWEB-26924)
    if (sid) params.sid = sid;
    if (ga) params.ga_cid = ga;
    return params;
  }

  /**
   * Envía un evento de búsqueda.
   * @param {string} type  'click' | 'autocomplete_select'
   * @param {object} data  { search_id, doc_id, position, collection, doc_type, title, site, component }
   * @param {object} opts  { apiUrl, apiKey } del componente (por defecto, los del orquestador)
   */
  function track(type, data, opts) {
    if (!data || !data.search_id) return false;
    opts = opts || {};
    var event = Object.assign({ type: type, page: window.location.pathname.slice(0, 300) }, data);
    var sid = getSessionId();
    if (sid) event.session_id = sid;
    var body = JSON.stringify({ key: opts.apiKey || API_KEY || undefined, events: [event] });
    var url = (opts.apiUrl || BASE_URL) + '/api/v1/events';
    try {
      // text/plain: petición simple, sin preflight CORS
      var blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
      if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return true;
    } catch (e) { /* fallback */ }
    try {
      fetch(url, { method: 'POST', body: body, keepalive: true, mode: 'no-cors',
                   headers: { 'Content-Type': 'text/plain;charset=UTF-8' } });
      return true;
    } catch (e) {
      return false;
    }
  }

  // --- Eventos GA4 al dataLayer del portal (aprobado por Marketing, Jira PWEB-25137) ---
  // Mismo formato que la tienda (lefebvre-datalayer.js) → GTM los recoge con su etiqueta genérica.
  // Solo si el portal tiene dataLayer y el componente lo pide (atributo ga4). El consentimiento lo gestiona GTM.
  var GA4_LISTS = { autocomplete: 'BIA - Autocompletado', results: 'BIA - Resultados' };

  /** Producto de BIA → item GA4. item_id = ID del portal (doc_id sin el prefijo «origin_»). */
  function ga4Item(r, index, listName) {
    var d = (r && r.data) || {};
    var id = String(d.id == null ? '' : d.id);
    if (d.origin && id.indexOf(d.origin + '_') === 0) id = id.slice(d.origin.length + 1);
    var price = d.price_min != null ? d.price_min : d.price;
    return {
      item_name: d.title || '',
      item_id: id,
      price: price != null ? String(price) : '-',
      item_brand: 'Lefebvre',
      item_category: (d.categories && d.categories[0]) || '-',
      item_variant: '-',
      item_list_name: listName,
      index: index,
      currency: 'EUR',
    };
  }

  /**
   * Empuja view_item_list / select_item al dataLayer.
   * @param {string} event      'view_item_list' | 'select_item'
   * @param {object} data       { component, search_id, items: [{ result, position }] } (position empieza en 1)
   */
  function ga4(event, data) {
    if (!window.dataLayer || !data || !data.items || !data.items.length) return false;
    var listName = GA4_LISTS[data.component] || 'BIA';
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: event,
      search_id: data.search_id || undefined,
      search_component: data.component,
      ecommerce: {
        item_list_id: listName.toLowerCase().replace(/\s*-\s*/g, '_').replace(/\s+/g, '_'),
        item_list_name: listName,
        items: data.items.map(function (it) { return ga4Item(it.result, it.position, listName); }),
      },
    });
    return true;
  }

  window.BIA = window.BIA || {};
  window.BIA.analytics = {
    track: track,
    searchParams: searchParams,
    hasConsent: hasStatsConsent,
    ga4: ga4,
  };

  // --- Inicialización ---
  function init() {
    console.log('[BIA] Orchestrator inicializado. Base URL:', BASE_URL, API_KEY ? '(api-key configurada)' : '(sin api-key)');
    scanDOM();
    observeDOM();
  }

  // Esperar a que el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
